import React, { useState } from 'react'
import MappingSelect from '../../Shared/Components/MappingSelect'
import MappingCheckbox from '../../Shared/Components/MappingCheckbox'
import Shared from '../../Shared/mappingUi.module.css'
import Style from '../multiLocation.module.css'

const EMPTY_DRAFT = {
    erpLocationId: '',
    erpMethod: '',
    ecommerceLocationId: '',
    ecommerceMethod: '',
    isDefault: false
}

/**
 * The "add a mapping" row. Owns only its own draft; a completed pair is handed to
 * the parent through onAdd and the draft resets. Each picker reports the location
 * id as its value and the label as the method name, which is what the save needs.
 */
const MultiLocationForm = ({ erpLocations, ecommerceLocations, isDisabled, isDuplicate, onAdd }) => {
    const [draft, setDraft] = useState(EMPTY_DRAFT)
    const [error, setError] = useState('')
    // Bumped after every add so both pickers remount and drop any custom-entry mode.
    const [formKey, setFormKey] = useState(0)

    const change = (changes) => {
        setDraft((current) => ({ ...current, ...changes }))
        setError('')
    }

    const handleAdd = () => {
        if (draft.erpLocationId === '' && draft.ecommerceLocationId === '') {
            setError('Select an ERP and an eCommerce location to map.')
            return
        }
        if (draft.erpLocationId === '') {
            setError('Select an ERP location.')
            return
        }
        if (draft.ecommerceLocationId === '') {
            setError('Select an eCommerce location.')
            return
        }
        if (isDuplicate(draft.erpLocationId, draft.ecommerceLocationId)) {
            setError(`"${draft.erpMethod}" is already mapped to "${draft.ecommerceMethod}".`)
            return
        }

        onAdd(draft)
        setDraft(EMPTY_DRAFT)
        setError('')
        setFormKey((current) => current + 1)
    }

    return (
        <div className={Shared.formPanel}>
            <div className={`${Shared.grid} ${Style.grid}`}>
                <label className={Shared.fieldLabel} htmlFor="erpLocation">ERP Location</label>
                <span />
                <label className={Shared.fieldLabel} htmlFor="ecommerceLocation">eCommerce Location</label>
                <span className={Shared.fieldLabel}>Default</span>
                <span />

                <MappingSelect
                    key={`erp-${formKey}`}
                    inputId="erpLocation"
                    options={erpLocations}
                    value={draft.erpLocationId}
                    placeholder="Select ERP Method"
                    customLabel="Custom ERP Method"
                    customPlaceholder="Enter ERP method"
                    hasError={error !== '' && draft.erpLocationId === ''}
                    isDisabled={isDisabled}
                    onChange={(value, option) => change({
                        erpLocationId: value,
                        erpMethod: option ? option.label : value
                    })}
                />
                <span className={Shared.arrow} aria-hidden="true">&rarr;</span>
                <MappingSelect
                    key={`ecommerce-${formKey}`}
                    inputId="ecommerceLocation"
                    options={ecommerceLocations}
                    value={draft.ecommerceLocationId}
                    placeholder="Select eCommerce Method"
                    customLabel="Custom eCommerce Method"
                    customPlaceholder="Enter eCommerce method"
                    hasError={error !== '' && draft.ecommerceLocationId === ''}
                    isDisabled={isDisabled}
                    onChange={(value, option) => change({
                        ecommerceLocationId: value,
                        ecommerceMethod: option ? option.label : value
                    })}
                />
                <MappingCheckbox
                    inputId="newLocationIsDefault"
                    label="Is Default ?"
                    checked={draft.isDefault}
                    isDisabled={isDisabled}
                    onChange={(checked) => change({ isDefault: checked })}
                />
                <button
                    type="button"
                    className={Shared.addButton}
                    title="Add mapping"
                    aria-label="Add mapping"
                    disabled={isDisabled}
                    onClick={handleAdd}
                >
                    <i className="uil-plus" aria-hidden="true" />
                </button>
            </div>
            {error ? <p className={Shared.formError} role="alert">{error}</p> : null}
        </div>
    )
}

export default MultiLocationForm
